import { useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { qcFailureLeaders, type QcLeaderWindow } from "@/lib/manufacturing/qcDashboardStats";
import type { QcDashboard } from "@/lib/manufacturing/qcDashboardClient";

const WINDOWS: { id: QcLeaderWindow; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "week", label: "This week" },
  { id: "month", label: "This month" },
];

function dayParts(iso: string): { weekday: string; month: string; date: string } {
  const [year, month, date] = iso.split("-").map(Number);
  const stamp = new Date(Date.UTC(year, (month || 1) - 1, date || 1));
  return {
    weekday: stamp.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }),
    month: stamp.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }),
    date: String(date || ""),
  };
}

function FailureWeekCard({ days }: { days: { day: string; count: number }[] }) {
  const today = days.at(-1)?.day;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <p className="mb-2 font-semibold">Last 7 days</p>
      <div className="grid grid-cols-7 gap-1 text-center">
        {days.map((row) => {
          const parts = dayParts(row.day);
          const isToday = row.day === today;
          return (
            <div
              key={row.day}
              className={
                "rounded-lg px-1 py-2 " +
                (isToday
                  ? "bg-gray-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
                  : "bg-gray-50 dark:bg-zinc-800")
              }
            >
              <p className="text-[11px] font-medium uppercase">{parts.weekday}</p>
              <p className="text-lg font-semibold leading-tight">{parts.date}</p>
              <p className="text-[11px]">{parts.month}</p>
              <p className="mt-1 text-sm font-semibold">{row.count}</p>
              <p className="text-[10px]">{row.count === 1 ? "failure" : "failures"}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function chosenClass(active: boolean): string {
  return active
    ? "rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-semibold text-white dark:bg-zinc-100 dark:text-zinc-900"
    : ghostBtnClass;
}

export function QcDashboardFailureStats({
  dashboard,
}: {
  dashboard: Pick<QcDashboard, "trends" | "leader_stamps">;
}) {
  const [window, setWindow] = useState<QcLeaderWindow>("today");
  const trend = dashboard.trends;
  const delta = trend.last_7_days - trend.prior_7_days;
  const leaders = qcFailureLeaders(dashboard.leader_stamps ?? [], window);
  return (
    <div className="space-y-4 text-sm text-gray-900 dark:text-zinc-100">
      <p>
        {trend.last_7_days} in the last 7 days
        {delta === 0 ? "" : delta > 0 ? ` · ${delta} more than the 7 days before` : ` · ${Math.abs(delta)} fewer than the 7 days before`}
        {trend.failures_per_unit_7d != null ? ` · ${trend.failures_per_unit_7d} per inspected unit` : ""}
      </p>
      <FailureWeekCard days={trend.per_day} />
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Failure leaders">
        {WINDOWS.map((choice) => (
          <button
            key={choice.id}
            type="button"
            role="tab"
            aria-selected={window === choice.id}
            className={chosenClass(window === choice.id)}
            onClick={() => setWindow(choice.id)}
          >
            {choice.label}
          </button>
        ))}
      </div>
      {leaders.hasLeader ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <LeaderList title="Failure modes" rows={leaders.modes} />
          <LeaderList title="Failure groups" rows={leaders.groups} />
        </div>
      ) : (
        <p>No statistical Failure Leaders</p>
      )}
    </div>
  );
}

function LeaderList({ title, rows }: { title: string; rows: { label: string; count: number }[] }) {
  return (
    <div className="space-y-1">
      <h4 className="font-semibold">{title}</h4>
      {rows.length === 0 ? <p>None in this period.</p> : (
        <ol className="list-decimal space-y-1 pl-5">
          {rows.map((row) => (
            <li key={row.label}>{row.label} · {row.count}</li>
          ))}
        </ol>
      )}
    </div>
  );
}
